// WALLET-P1 §6.2: Operator › Billing (`/operator/billing?tab=&month=`): the platform's price
// list and bonus ladder, each granted company's wallet (custom prices, credit, refunds, the
// switch) and the month's margin; the month picker serves Companies and Margin.
// BILLING-PLANS §6.2 joins its tabs: Plans (the plan and maintenance price lists, a platform
// admin edits them), Subscriptions (each granted company's plan, collection, discount, tiers
// and projects) and Invoices (across the granted companies).
import { Button, LocationHeader, NativeSelect, NativeSelectOption, ViewTabs } from "@companyos/ui";
import { CreditCardIcon, PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";
import type { Api } from "../../../api/client";
import { monthName, recentUtcMonths } from "../../billing/billing-queries";
import { ONE_TIME_CHARGES } from "../../billing/flags";
import { OpsBody, OpsFrame, operatorCrumb } from "../../ops-ux";
import { Companies } from "./companies";
import { Discounts } from "./discounts";
import { InvoicesTab, NewDocumentDialog } from "./invoices";
import { Margin } from "./margin";
import { PlansTab } from "./plans";
import { PriceList } from "./price-list";
import { CompaniesTab as SubscriptionsTab } from "./subscriptions";

export const BILLING_TABS = [
  "prices",
  "discounts",
  "plans",
  "subscriptions",
  "invoices",
  "companies",
  "margin",
] as const;
export type BillingTab = (typeof BILLING_TABS)[number];

const LABELS: Record<BillingTab, string> = {
  prices: "Price list",
  discounts: "Discounts",
  plans: "Plans",
  subscriptions: "Subscriptions",
  invoices: ONE_TIME_CHARGES ? "Invoices & quotes" : "Invoices",
  companies: "Companies",
  margin: "Margin",
};

export function OperatorBillingPage({
  api,
  tab,
  month,
  company,
  onChange,
}: {
  api: Api;
  tab: BillingTab;
  month: string;
  /** Subscriptions: the company to open expanded (an operator's inbox item links here). */
  company?: string | undefined;
  onChange: (next: { tab: BillingTab; month: string }) => void;
}) {
  const months = useMemo(() => {
    const list = recentUtcMonths(new Date());
    return list.includes(month) ? list : [month, ...list];
  }, [month]);
  const [creating, setCreating] = useState(false);
  const monthly = tab === "companies" || tab === "margin";
  return (
    <OpsFrame data-slot="operator-billing">
      <LocationHeader
        crumbs={[operatorCrumb, { label: "Billing" }]}
        icon={CreditCardIcon}
        title="Billing"
        {...(monthly
          ? {
              actions: (
                <NativeSelect
                  aria-label="Month"
                  size="sm"
                  value={month}
                  onChange={(e) => onChange({ tab, month: e.target.value })}
                >
                  {months.map((m) => (
                    <NativeSelectOption key={m} value={m}>
                      {monthName(m)}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              ),
            }
          : tab === "invoices" && ONE_TIME_CHARGES
            ? {
                actions: (
                  <Button size="sm" onClick={() => setCreating(true)}>
                    <PlusIcon data-icon="inline-start" />
                    New
                  </Button>
                ),
              }
            : {})}
      />
      <div className="px-6 max-md:px-4">
        <ViewTabs
          aria-label="Billing"
          tabs={BILLING_TABS.map((t) => ({ value: t, label: LABELS[t] }))}
          value={tab}
          onValueChange={(v) => onChange({ tab: v as BillingTab, month })}
        />
      </div>
      <OpsBody>
        {tab === "prices" ? (
          <PriceList api={api} />
        ) : tab === "discounts" ? (
          <Discounts api={api} />
        ) : tab === "plans" ? (
          <PlansTab api={api} />
        ) : tab === "subscriptions" ? (
          <SubscriptionsTab api={api} {...(company ? { initialOpen: company } : {})} />
        ) : tab === "invoices" ? (
          <InvoicesTab api={api} />
        ) : tab === "companies" ? (
          <Companies api={api} month={month} />
        ) : (
          <Margin api={api} month={month} />
        )}
      </OpsBody>
      {ONE_TIME_CHARGES && creating ? (
        <NewDocumentDialog api={api} onClose={() => setCreating(false)} />
      ) : null}
    </OpsFrame>
  );
}
